// L'ÉCRAN DU PARCOURS (30/07) : la carte de progression, en surcouche DOM.
//
// Même famille de contrat que makePickerUi : un MenuHandle avec destroy(), pour se ranger dans
// l'état `menu` de main.ts. Le squelette est dans public/index.html (#parcours), le dessin des
// pastilles dans client/parcours-dessin.ts — partagé avec la planche de validation.
//
// ⚠️ CET ÉCRAN N'EST PAS L'AUTORITÉ. Il affiche ce que /api/parcours lui donne, y compris l'état
// de chaque nœud. Un joueur hors ligne ou non connecté ne voit pas la carte du tout (elle n'aurait
// aucun sens sans progression) : main.ts retombe alors sur le mode libre.
import { MONDES, type EtatNoeud, type GenreNoeud } from '../shared/parcours'
import { dispositionParcours, jeton, peindreBande, type MondeCarte } from './parcours-carte'
import type { MenuHandle } from './screens'

export interface NoeudDto {
  slug: string
  titre: string
  genre: GenreNoeud
  etat: EtatNoeud
  echecs: number
}
export interface MondeDto {
  cle: string; nom: string; lecon: string; monde: string
  mecaniques: readonly string[]; recompense: string | null; accent: string
  noeuds: readonly NoeudDto[]
}
export interface ParcoursDto {
  mondes: readonly MondeDto[]
  avancement: { finis: number; total: number }
  suivant: string | null
  echecsAvantCle: number
}

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T

/** Le libellé sous une pastille verrouillée : dire COMBIEN il reste vaut mieux qu'un cadenas muet. */
function aide(n: NoeudDto, precEchecs: number, seuil: number): { texte: string; cle: boolean } | null {
  if (n.etat === 'cle') return { texte: '🔑 tu peux sauter', cle: true }
  if (n.etat !== 'verrouille') return null
  const reste = seuil - precEchecs
  if (precEchecs > 0 && reste > 0) return { texte: `encore ${reste} essai${reste > 1 ? 's' : ''}`, cle: false }
  return null
}

export function makeParcoursUi(
  onJouer: (slug: string) => void,
  onBack: () => void,
): MenuHandle & { update(dto: ParcoursDto): void } {
  const root = $('parcours')
  const hote = $('pc-mondes')
  const avance = $('pc-avance')
  const back = $<HTMLButtonElement>('pc-back')
  root.hidden = false

  /** UNE BANDE = LA FENÊTRE D'UN MONDE sur la carte unique. `rang` est sa place dans l'archipel,
   *  `decor` l'index du vrai monde (0-7) : un monde encore vide est sauté, les deux ne coïncident
   *  donc pas toujours. */
  interface Bande {
    rang: number; decor: number; n: number
    piste: HTMLElement; sec: HTMLElement; fond: HTMLCanvasElement; boutons: HTMLButtonElement[]
  }
  const bandes: Bande[] = []
  const cartes: MondeCarte[] = []

  /* LE TRACÉ, en UN SEUL passage pour tout l'archipel (01/08). Ce n'est pas un détail
     d'organisation : la géométrie se calcule une fois, en pixels globaux, et sert à la fois au
     dessin de chaque fenêtre et au placement des boutons. C'est la seule façon d'obtenir une
     carte qui n'en fait qu'une — un calcul par monde laissait une couture à chaque jointure. */
  function tout(): void {
    if (bandes.length === 0) return
    // ⚠️ LA MESURE FRACTIONNAIRE, PAS `clientWidth`. Une bande fait 335,39 px de haut ; arrondie
    // à 335, elle laissait un liseré de 0,39 px de fond de page en bas de CHAQUE monde — un trait
    // noir en travers de la mer, huit fois. La boîte est fractionnaire, le canvas doit l'être.
    const boite = bandes[0]!.piste.getBoundingClientRect()
    const w = boite.width, h = boite.height
    if (w < 2 || h < 2) return
    const disp = dispositionParcours(bandes.map((b) => ({ n: b.n, decor: b.decor })), w, h)
    const dpr = Math.min(3, window.devicePixelRatio || 1)
    for (const b of bandes) {
      b.fond.width = Math.round(w * dpr); b.fond.height = Math.round(h * dpr)
      // Pas de taille CSS : `.pc-ile` est en `inset: 0`, il épouse la boîte au sous-pixel près.
      const g = b.fond.getContext('2d')
      if (!g) continue
      g.setTransform(b.fond.width / w, 0, 0, b.fond.height / h, 0, 0)
      peindreBande(g, b.rang, disp, cartes)
      const pos = disp.etapes[b.rang] ?? []
      const cadre = b.piste.getBoundingClientRect()
      b.boutons.forEach((bt, i) => {
        const pt = pos[i]
        if (!pt) return
        bt.style.left = (pt.x / w * 100) + '%'
        bt.style.top = ((pt.y - b.rang * h) / h * 100) + '%'
        // ⚠️ L'ÉTIQUETTE RENTRE DANS LE CADRE. Elle est centrée sous son jeton et ne se coupe pas
        // (`nowrap`) : sur une étape proche d'un bord, « Arène : face à face » sortait de la piste
        // et se faisait rogner net par l'`overflow: hidden`. On la décale du minimum nécessaire.
        const et = bt.querySelector<HTMLElement>('.pc-etiq')
        if (!et) return
        et.style.transform = 'translateX(-50%)'
        const r = et.getBoundingClientRect()
        const d = r.left < cadre.left + 4 ? cadre.left + 4 - r.left
          : r.right > cadre.right - 4 ? cadre.right - 4 - r.right : 0
        if (d) et.style.transform = `translateX(calc(-50% + ${Math.round(d)}px))`
      })
      // Le titre se met du côté OPPOSÉ à l'île, sinon il lui tombe dessus sur un grand écran.
      b.sec.classList.toggle('titre-droite', disp.iles[b.rang]?.gauche ?? true)
    }
  }

  function rendre(dto: ParcoursDto): void {
    hote.textContent = ''
    bandes.length = 0
    cartes.length = 0
    avance.textContent = `${dto.avancement.finis} / ${dto.avancement.total}`

    dto.mondes.forEach((m, iM) => {
      if (m.noeuds.length === 0) return          // un monde encore vide ne s'affiche pas
      const accent = m.accent
      const sec = document.createElement('section')
      sec.className = 'pc-monde'

      const tete = document.createElement('div')
      tete.className = 'pc-tete'
      const num = document.createElement('p')
      num.className = 'pc-num'; num.style.color = accent
      num.textContent = `Monde ${iM + 1}`
      const h = document.createElement('h3'); h.textContent = m.nom
      const lec = document.createElement('p'); lec.className = 'pc-lecon'; lec.textContent = m.lecon
      tete.append(num, h, lec)
      // Le compte d'étapes, dans l'en-tête et non sur l'île : posé sur la carte, il tombait sur
      // l'étiquette du boss, la plus large de toutes.
      const faits = m.noeuds.filter((n) => n.etat === 'fini').length
      const pas = document.createElement('p')
      pas.className = 'pc-etapes'
      pas.textContent = `${faits} / ${m.noeuds.length} étapes`
      tete.appendChild(pas)
      if (m.recompense) {
        const prix = document.createElement('p')
        prix.className = 'pc-prix'
        prix.innerHTML = `<b>Récompense du boss :</b> ${m.recompense}`
        tete.appendChild(prix)
      }
      sec.appendChild(tete)

      // LA FENÊTRE DU MONDE (01/08) : un canvas qui peint sa part de LA carte — mer, îles voisines
      // comprises, décors, ruban continu, progression — et les étapes posées dessus en boutons
      // DOM. On garde les boutons pour les clics, le focus clavier et les lecteurs d'écran ; seul
      // le décor est du canvas.
      const piste = document.createElement('div')
      piste.className = 'pc-piste'
      const fond = document.createElement('canvas')
      fond.className = 'pc-ile'
      piste.appendChild(fond)

      const boutons: HTMLButtonElement[] = []
      // ⚠️ Le fond et les boutons se placent APRÈS la mise en page, dans `tout()` : la largeur
      // vient du CSS, pas l'inverse.
      bandes.push({ rang: bandes.length, decor: iM, n: m.noeuds.length, piste, sec, fond, boutons })
      cartes.push({ accent })

      let numero = 0
      m.noeuds.forEach((n, i) => {
        if (n.genre !== 'bonus') numero++
        const b = document.createElement('button')
        b.type = 'button'
        b.className = `pc-noeud ${n.genre} ${n.etat}`
        boutons.push(b)                          // placé par le traceur, à la taille réelle
        const cv = document.createElement('canvas')
        jeton(cv, { genre: n.genre, etat: n.etat, numero })
        b.appendChild(cv)
        const et = document.createElement('span')
        et.className = 'pc-etiq'; et.textContent = n.titre
        b.appendChild(et)

        // le précédent NORMAL, pour savoir combien d'essais il reste avant la clé
        let prec: NoeudDto | undefined
        for (let k = i - 1; k >= 0; k--) if (m.noeuds[k]!.genre !== 'bonus') { prec = m.noeuds[k]; break }
        const a = aide(n, prec?.echecs ?? 0, dto.echecsAvantCle)
        if (a) {
          const s = document.createElement('span')
          s.className = `pc-aide${a.cle ? ' cle' : ''}`; s.textContent = a.texte
          b.appendChild(s)
        }

        const ouvert = n.etat !== 'verrouille'
        b.disabled = !ouvert
        b.setAttribute('aria-label',
          `${n.titre} — ${n.etat === 'fini' ? 'terminé' : n.etat === 'verrouille' ? 'verrouillé' : 'à jouer'}`)
        if (ouvert) b.addEventListener('click', () => onJouer(n.slug))
        piste.appendChild(b)
      })
      sec.appendChild(piste)
      hote.appendChild(sec)
    })

    /* Les mondes encore vides : les annoncer plutôt que de les cacher. Un parcours qui s'arrête
       net donne l'impression d'un jeu fini ; « la suite arrive » dit la vérité. */
    const restants = MONDES.length - dto.mondes.filter((m) => m.noeuds.length > 0).length
    if (restants > 0) {
      const p = document.createElement('p')
      p.className = 'pc-avenir'
      p.textContent = restants === 1
        ? 'Un autre monde est en préparation.'
        : `${restants} autres mondes sont en préparation.`
      hote.appendChild(p)
    }

    /* La carte se trace APRÈS la mise en page, et se rejoue quand elle peut encore bouger : tant
       que la police n'est pas résolue, les étiquettes ont une hauteur provisoire et la hauteur
       des bandes glisse. `tout()` repeint tout à chaque fois, donc c'est idempotent. */
    requestAnimationFrame(tout)
    document.fonts?.ready.then(tout).catch(() => undefined)
  }

  const surResize = (): void => { tout() }
  window.addEventListener('resize', surResize)
  const surBack = (): void => onBack()
  back.addEventListener('click', surBack)
  const surTouche = (e: KeyboardEvent): void => { if (e.key === 'Escape') onBack() }
  window.addEventListener('keydown', surTouche)

  return {
    update: rendre,
    destroy(): void {
      window.removeEventListener('resize', surResize)
      window.removeEventListener('keydown', surTouche)
      back.removeEventListener('click', surBack)
      bandes.length = 0; cartes.length = 0
      hote.textContent = ''
      root.hidden = true
    },
  }
}
